"use client";

import { useRef, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import LoadingBar, { LoadingBarRef } from "react-top-loading-bar";
import { NAVIGATION_START_EVENT } from "@/lib/navigation-feedback";

export function TopLoadingBar() {
  const ref = useRef<LoadingBarRef>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentUrlRef = useRef("");

  useEffect(() => {
    currentUrlRef.current = `${pathname}?${searchParams.toString()}`;

    ref.current?.complete();
  }, [pathname, searchParams]);

  useEffect(() => {
    const start = () => ref.current?.continuousStart();

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;

      if (
        anchor.target ||
        anchor.hasAttribute("download") ||
        anchor.getAttribute("rel") === "external"
      ) {
        return;
      }

      const nextUrl = new URL(anchor.href);
      if (nextUrl.origin !== window.location.origin) return;

      const nextPath = `${nextUrl.pathname}?${nextUrl.searchParams.toString()}`;
      if (nextPath === currentUrlRef.current) return;

      start();
    };

    window.addEventListener(NAVIGATION_START_EVENT, start);
    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, start);
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, []);

  return (
    <LoadingBar
      ref={ref}
      height={2}
      shadow={true}
      color="hsl(var(--brand-primary))"
    />
  );
}
